import React, { useEffect, useState } from 'react';

// Cookie consent banner (GDPR) wired to Google Consent Mode v2. public/index.html sets
// everything to "denied" before the GTM containers load and re-applies a saved choice;
// this component asks the visitor, saves the choice and updates the consent state.
const STORAGE_KEY = 'dd_cookie_consent';
const OPEN_EVENT = 'dd:open-cookie-settings';

const font = "'Space Grotesk',sans-serif";
const mono = "'JetBrains Mono',monospace";

export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_EVENT));

function readConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

function applyConsent({ analytics, marketing }) {
  const value = (on) => (on ? 'granted' : 'denied');
  window.dataLayer = window.dataLayer || [];
  // gtag() pushes the `arguments` object — GTM only reads consent commands in that shape
  function gtag() { window.dataLayer.push(arguments); } // eslint-disable-line prefer-rest-params
  gtag('consent', 'update', {
    analytics_storage: value(analytics),
    ad_storage: value(marketing),
    ad_user_data: value(marketing),
    ad_personalization: value(marketing),
  });
  // Lets GTM triggers react to the choice without a page reload
  window.dataLayer.push({ event: 'cookie_consent_update', analytics, marketing });
}

function saveConsent(choice) {
  const record = { ...choice, date: new Date().toISOString(), version: 1 };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch { /* storage unavailable */ }
  applyConsent(choice);
}

const Toggle = ({ label, desc, checked, onChange, locked }) => (
  <label style={{
    display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px',
    background: '#0d0e12', border: '1px solid #282a36', borderRadius: 10,
    cursor: locked ? 'default' : 'pointer',
  }}>
    <input type="checkbox" checked={checked} disabled={locked} onChange={e => onChange?.(e.target.checked)}
      style={{ marginTop: 3, accentColor: '#bd93f9', width: 16, height: 16, flexShrink: 0 }} />
    <span>
      <span style={{ display: 'block', color: '#f8f8f2', fontSize: 14, fontWeight: 600, fontFamily: font }}>
        {label}{locked && <span style={{ color: '#6272a4', fontWeight: 400, fontSize: 12 }}> · mereu active</span>}
      </span>
      <span style={{ display: 'block', color: '#8b8fa3', fontSize: 12, lineHeight: 1.5, marginTop: 2, fontFamily: font }}>{desc}</span>
    </span>
  </label>
);

const CookieBanner = () => {
  const [open, setOpen] = useState(() => !readConsent());
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(() => !!readConsent()?.analytics);
  const [marketing, setMarketing] = useState(() => !!readConsent()?.marketing);

  useEffect(() => {
    const show = () => {
      const saved = readConsent();
      setAnalytics(!!saved?.analytics);
      setMarketing(!!saved?.marketing);
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);

  if (!open) return null;

  const choose = (choice) => { saveConsent(choice); setOpen(false); };
  const btn = {
    border: 'none', borderRadius: 8, padding: '11px 18px', cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: font, whiteSpace: 'nowrap',
  };

  return (
    <div role="dialog" aria-live="polite" aria-label="Setări cookie-uri" style={{
      position: 'fixed', left: 16, right: 16, bottom: 16, zIndex: 1000,
      maxWidth: 560, marginLeft: 'auto',
      background: 'rgba(17,18,24,.97)', border: '1px solid #bd93f940', borderRadius: 14,
      boxShadow: '0 20px 60px rgba(0,0,0,.5), 0 0 40px rgba(189,147,249,.12)',
      backdropFilter: 'blur(12px)', padding: 'clamp(16px,4vw,22px)',
      maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>🍪</span>
        <span style={{ color: '#f8f8f2', fontSize: 16, fontWeight: 700, fontFamily: font }}>Cookie-uri</span>
        <span style={{ color: '#44475a', fontSize: 11, fontFamily: mono, marginLeft: 'auto' }}>fără colți ascunși</span>
      </div>
      <p style={{ color: '#a0a4b8', fontSize: 13, lineHeight: 1.6, margin: '0 0 14px', fontFamily: font }}>
        Folosim cookie-uri necesare ca site-ul să funcționeze. Cu acordul tău, folosim și cookie-uri
        de statistică, ca să vedem ce pagini sunt utile, și de marketing, ca să măsurăm reclamele.
        Poți schimba oricând alegerea din „Setări cookie-uri”, în subsolul paginii.
      </p>

      {details && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          <Toggle label="Necesare" locked checked
            desc="Funcționarea site-ului și memorarea alegerii tale despre cookie-uri." />
          <Toggle label="Statistici" checked={analytics} onChange={setAnalytics}
            desc="Date anonime despre vizite (de exemplu Google Analytics), ca să îmbunătățim site-ul." />
          <Toggle label="Marketing" checked={marketing} onChange={setMarketing}
            desc="Măsurarea și personalizarea reclamelor (de exemplu Google Ads, Tanganica)." />
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button type="button" onClick={() => choose({ analytics: true, marketing: true })} style={{
          ...btn, flex: '1 1 140px', background: 'linear-gradient(135deg,#50fa7b,#8be9fd)', color: '#0d0e12',
        }}>Accept toate</button>
        {details ? (
          <button type="button" onClick={() => choose({ analytics, marketing })} style={{
            ...btn, flex: '1 1 140px', background: '#bd93f920', color: '#bd93f9', border: '1px solid #bd93f950',
          }}>Salvează alegerea</button>
        ) : (
          <button type="button" onClick={() => setDetails(true)} style={{
            ...btn, flex: '1 1 100px', background: 'transparent', color: '#8be9fd', border: '1px solid #282a36',
          }}>Setări</button>
        )}
        <button type="button" onClick={() => choose({ analytics: false, marketing: false })} style={{
          ...btn, flex: '1 1 120px', background: '#282a36', color: '#f8f8f2',
        }}>Doar necesare</button>
      </div>
    </div>
  );
};

export default CookieBanner;
